import { useQuery } from "@apollo/client/react";
import { Box, Card, Flex, PrimaryButton, QuietButton } from "@nulogy/components";
import Form from "components/controls/Form";
import Textarea from "components/controls/Textarea";
import useUpdateWorkOrder from "hooks/apollo/workOrder/useUpdateWorkOrder";
import { GetWorkOrder } from "hooks/apollo/workOrder/workOrder.gql";
import useNotesPopUp from "hooks/state/useNotesPopUp";
import React from "react";
import styled, { useTheme } from "styled-components";

const NAVBAR_HEIGHT = 56;
const FOOTER_HEIGHT = 40;
const POP_UP_HEIGHT = 120;
const POP_UP_WIDTH = 240;
const DEFAULT_POP_UP_TOP_OFFSET = 4;
const POP_UP_DISPLAY_HORIZONTAL_OFFSET = 61;
const LINE_RESIZE_HANDLE_WIDTH = 5;

function calculatePopUpLeftPosition({ workBlockLocation, teardownTimeWidth, scheduleLocation, lineLabelWidth }) {
  const popUpLeftPosition = workBlockLocation.right - teardownTimeWidth;

  const popUpRightEdge = popUpLeftPosition + POP_UP_WIDTH - POP_UP_DISPLAY_HORIZONTAL_OFFSET;
  const scheduleRightEdge = scheduleLocation.right;

  const scheduleLeftEdge = scheduleLocation.left + lineLabelWidth + LINE_RESIZE_HANDLE_WIDTH;
  const workBlockSpansEntireSchedule =
    workBlockLocation.left <= scheduleLeftEdge && workBlockLocation.right >= scheduleLocation.right;

  if (workBlockSpansEntireSchedule) {
    return scheduleLocation.width / 2;
  }

  if (popUpRightEdge > scheduleRightEdge) {
    return workBlockLocation.left - POP_UP_WIDTH + POP_UP_DISPLAY_HORIZONTAL_OFFSET;
  }

  return popUpLeftPosition - POP_UP_DISPLAY_HORIZONTAL_OFFSET;
}

function calculatePopUpTopPosition({ workBlockLocation, scheduleLocation }) {
  const popUpTopPosition = workBlockLocation.bottom - NAVBAR_HEIGHT;
  let popUpTopOffset = DEFAULT_POP_UP_TOP_OFFSET;

  const popUpBottomEdge = popUpTopPosition + POP_UP_HEIGHT;
  const scheduleBottomEdge = scheduleLocation.bottom - FOOTER_HEIGHT;

  if (popUpBottomEdge > scheduleBottomEdge) {
    popUpTopOffset = POP_UP_HEIGHT + workBlockLocation.height;
  }

  return popUpTopPosition - popUpTopOffset;
}

const NotesCard = styled(Card)(({ isOpen, leftPosition, topPosition, theme }) => ({
  backgroundColor: theme.colors.white,
  display: isOpen ? "block" : "none",
  left: `${leftPosition}px`,
  minHeight: `${POP_UP_HEIGHT}px`,
  minWidth: `${POP_UP_WIDTH}px`,
  padding: theme.space.x1,
  position: "absolute",
  top: `${topPosition}px`,
  zIndex: 10,
}));

export default function NotesPopUp() {
  const { workOrderId, workBlockId, isOpen, close } = useNotesPopUp();
  const updateWorkOrder = useUpdateWorkOrder();
  const { data } = useQuery(GetWorkOrder, {
    skip: !workOrderId,
    variables: { id: workOrderId },
  });
  const theme = useTheme();

  if (!data) return null;

  const workBlockElement = document.getElementsByClassName(`work-block-${workBlockId}`)[0];
  if (!workBlockElement) return null;

  const workBlockLocation = workBlockElement.getBoundingClientRect();
  const teardownTimeElement = workBlockElement.getElementsByClassName("teardown-time")[0];
  const teardownTimeWidth = teardownTimeElement ? teardownTimeElement.getBoundingClientRect().width : 0;
  const lineLabelWidth = document.getElementsByClassName("schedule-line-cell")[0].getBoundingClientRect().width;
  const scheduleLocation = document.getElementById("bryntum-scheduler").getBoundingClientRect();
  const leftPosition = calculatePopUpLeftPosition({
    lineLabelWidth,
    scheduleLocation,
    teardownTimeWidth,
    workBlockLocation,
  });
  const topPosition = calculatePopUpTopPosition({ scheduleLocation, workBlockLocation });

  const handleSave = ({ notes }) => {
    updateWorkOrder({ id: workOrderId, notes });
    close();
  };

  const handleBeforeSave = (values, actions) => {
    actions.setFieldValue("notes", values.notes.trim());

    return values;
  };

  return (
    <NotesCard className="spec-edit-notes-pop-up" isOpen={isOpen} leftPosition={leftPosition} topPosition={topPosition}>
      <Form
        enableReinitialize
        initialValues={{ notes: data.workOrder.notes || "" }}
        onBeforeSave={handleBeforeSave}
        onSave={handleSave}
      >
        <Box marginBottom="x1">
          <Textarea
            field="notes"
            id="work-order-details-notes"
            rows={3}
            style={{
              borderColor: theme.colors.grey,
              color: theme.colors.black,
              fontSize: theme.fontSizes.small,
              lineHeight: theme.lineHeights.smallTextCompressed,
              minHeight: "72px",
              minWidth: "224px",
            }}
          />
        </Box>
        <Flex>
          <PrimaryButton mr="half" size="small" type="submit">
            Save
          </PrimaryButton>
          <QuietButton onClick={close} size="small" type="button">
            Close
          </QuietButton>
        </Flex>
      </Form>
    </NotesCard>
  );
}
